/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, {
  useEffect, useReducer, useRef, useState, forwardRef, useImperativeHandle
} from 'react';
import Highcharts from 'highcharts';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '@services/firebase-service';
import { updateWordCloud } from '@services/youtube-platform.service';
import { logToCloudWatch } from '@services/logger.service';
import { useStreamData } from '@lib/hooks/useStreamData';
import { pushDataLayerForEvent } from '@lib/gtag';
import WordChart from './WordChart';
import { handleEngagement } from '@services/interactions-service';
import { chromaPaletteParser } from './helper/chromaPaletteParser';
import { colorPalette } from '@lib/constants';
import useFonts from '@lib/hooks/useFonts';

const Wordcloud = forwardRef(({
  streamId,
  platformType,
  currentRosFeature,
  currentRosResult,
  interactionType,
  setCurrentFeatureId,
  startInteraction,
  splitDiv,
  dispatchTab,
  comments,
  isFullScreen,
  setShowRosModel,
  interactionState,
  moderationModeRef,
  setMessages,
  activeInteractionRef,
  updateFSMeta,
  setShowNotification,
  setStartTime,
  showInstantCustomisationPopup = false,
  setTotalResponders
}, ref) => {
  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setOverrideDismissStatus,
    setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setOverrideDismissStatus: s.setOverrideDismissStatus,
    setFeatureId: s.setFeatureId
  }));

  const { isRos, isResult } = interactionType;
  const [wordCloudId, setWordCloudId] = useState('');
  const [wordCloudColors, setWordCloudColors] = useState(currentRosFeature.color);
  const [wordCloudSettings, setwordCloudSettings] = useState(currentRosFeature.setting);

  const [isPaused, _setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);
  const setIsPaused = (data) => {
    isPausedRef.current = data;
    _setIsPaused(data);
  };

  const [exclusionList, _setExclusionList] = useState([]);
  const exclusionListRef = useRef(exclusionList);
  const setExclusionList = (data) => {
    exclusionListRef.current = [...exclusionListRef.current, data];
    _setExclusionList([...exclusionList, data]);
  };

  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(wordCloudSettings?.FontStyle || 'Sans-Serif');

  const [gtagTriggerCounter, setGtagTriggerCounter] = useState(0);

  const newPalette = chromaPaletteParser().parse(colorPalette, wordCloudColors?.wcBgColor);

  const getWordCloudData = () => {
    if (currentRosResult.length > 0 && currentRosResult[0].comments.length > 0) {
      return currentRosResult[0].comments;
    }
    return currentRosFeature.comments;
  };

  const highchartsRef = useRef(null);

  const options = {
    exporting: { enabled: false },
    chart: {
      backgroundColor: 'rgba(255, 255, 255, 0.0)'
    },
    plotOptions: {
      series: {},
      chart: { height: '100%' },
      wordcloud: {
        rotation: {
          from: 0,
          orientations: wordCloudSettings?.Vertical ? 2 : 1,
          to: wordCloudSettings?.Vertical ? 90 : 0
        },
        color: wordCloudColors?.wcWordColor || '#319795',
        colorByPoint: wordCloudColors?.multipleColors,
        colors: wordCloudColors?.multipleColors ? newPalette : [wordCloudColors?.wcWordColor || '#319795'],
        style: {
          fontFamily
        }
      }
    },

    credits: {
      enabled: false
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          legend: {
            enabled: false
          }
        }
      }]
    },
    series: [{
      type: 'wordcloud',
      data: getWordCloudData(),
      name: 'Count'
    }],
    title: { text: '' }
  };

  const [chartOptions, dispatch] = useReducer((state, action) => {
    if (action?.data?.length > 0) {
      setGtagTriggerCounter(1);
      if (gtagTriggerCounter === 0) pushDataLayerForEvent('populate_comments_wonder_words');
    }

    if (action.type === 'UPDATE_COLOR') {
      return {
        ...state,
        series: [{
          type: 'wordcloud',
          data: [
            ...(state.series[0]?.data || []),
            ...(action?.data || []),
          ],
          name: 'Count'
        }],
        chart: {
          backgroundColor: 'rgba(255, 255, 255, 0.0)'
        },
        credits: {
          enabled: false
        },
        responsive: {
          rules: [{
            condition: {
              maxWidth: 500
            },
            chartOptions: {
              legend: {
                enabled: false
              }
            }
          }]
        },
        plotOptions: {
          series: {},
          chart: { height: '100%' },
          wordcloud: {
            rotation: {
              from: 0,
              orientations: wordCloudSettings?.Vertical ? 2 : 1,
              to: wordCloudSettings?.Vertical ? 90 : 0
            },
            color: action?.colors?.wcWordColor || wordCloudColors?.wcWordColor || '#319795',
            colorByPoint: wordCloudColors?.multipleColors,
            colors: wordCloudColors?.multipleColors ? newPalette : [action?.colors?.wcWordColor || wordCloudColors?.wcWordColor || '#319795'],
            style: {
              fontFamily
            }
          }
        }
      };
    }

    if (action.type === 'UPDATE_SETTING') {
      return {
        ...state,
        series: [{
          type: 'wordcloud',
          data: [
            ...(state.series[0]?.data || []),
            ...(action?.data || []),
          ],
          name: 'Count'
        }],
        chart: {
          backgroundColor: 'rgba(255, 255, 255, 0.0)'
        },
        credits: {
          enabled: false
        },
        responsive: {
          rules: [{
            condition: {
              maxWidth: 500
            },
            chartOptions: {
              legend: {
                enabled: false
              }
            }
          }]
        },
        plotOptions: {
          series: {},
          chart: { height: '100%' },
          wordcloud: {
            rotation: {
              from: 0,
              orientations: action?.settings?.Vertical || wordCloudSettings?.Vertical ? 2 : 1,
              to: (action?.settings?.Vertical || wordCloudSettings?.Vertical) ? 90 : 0
            },
            color: wordCloudColors?.wcWordColor || '#319795',
            colorByPoint: wordCloudColors?.multipleColors,
            colors: wordCloudColors?.multipleColors ? newPalette : [wordCloudColors?.wcWordColor || '#319795'],
            style: {
              fontFamily
            }
          }
        }
      };
    }

    return {
      ...state,
      series: [{
        type: 'wordcloud',
        data: action?.data || [],
        name: 'Count'
      }],
      chart: {
        backgroundColor: 'rgba(255, 255, 255, 0.0)'
      },
      credits: {
        enabled: false
      },
      responsive: {
        rules: [{
          condition: {
            maxWidth: 500
          },
          chartOptions: {
            legend: {
              enabled: false
            }
          }
        }]
      },
      plotOptions: {
        series: {},
        chart: { height: '100%' },
        wordcloud: {
          rotation: {
            from: 0,
            orientations: wordCloudSettings?.Vertical ? 2 : 1,
            to: wordCloudSettings?.Vertical ? 90 : 0
          },
          color: wordCloudColors?.wcWordColor || '#319795',
          colorByPoint: wordCloudColors?.multipleColors,
          colors: wordCloudColors?.multipleColors ? newPalette : [wordCloudColors?.wcWordColor || '#319795'],
          style: {
            fontFamily
          }
        }
      }
    };
  }, options);

  const setPreviousData = ({ interactionResult = [] }) => {
    if (interactionResult.length > 0 && interactionResult[0].comments.length > 0) {
      dispatch({ type: 'UPDATE_DATA', data: interactionResult[0].comments });
    } else {
      dispatch({ type: 'UPDATE_DATA', data: [] });
    }
  };

  const setupWordCloud = ({ id, settings, colors, noOfResponders, interactionResult }) => {
    dispatch({ data: [] });

    setFeatureId(id);
    setCurrentFeatureId(id);
    setWordCloudId(id);
    setWordCloudColors(colors);
    setwordCloudSettings(settings);

    setPreviousData({ interactionResult });

    setTotalResponders(noOfResponders);
  };

  const handleWordCloud = async ({ interactionId = null, interactionResult = [] }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });

    if (response.status && response.entity) {
      const interactionDetails = {
        id: response.entity.engagementId,
        settings: currentRosFeature.setting,
        colors: currentRosFeature.color,
        noOfResponders: response.entity.noOfResponders || 0,
        interactionResult
      };
      setupWordCloud(interactionDetails);
      updateFSMeta({ activeInteraction: { ...interactionDetails, type: 'wordCloud' } });
    }
  };

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };

  useEffect(() => {
    if (currentRosFeature.color) {
      setWordCloudColors(currentRosFeature.color);
      dispatch({ type: 'UPDATE_COLOR', colors: currentRosFeature.color, data: currentRosFeature.comments });
    }
  }, [currentRosFeature.color]);

  useEffect(() => {
    if (currentRosFeature.setting) {
      setwordCloudSettings(currentRosFeature.setting);
      dispatch({ type: 'UPDATE_SETTING', settings: currentRosFeature.setting, data: currentRosFeature.comments });
    }
  }, [currentRosFeature.setting]);

  useEffect(() => {
    setPreviousData({ interactionResult: currentRosResult });
  }, [currentRosFeature.id, currentRosResult]);

  useEffect(() => {
    if (wordCloudId && isRos) {
      const logData = {
        streamId,
        interactionType: 'wordcloud',
        interactionId: wordCloudId
      };
      setFirestoreUnsub({
        unsub: onSnapshot(doc(db, 'streams', streamId, 'wordcloud', wordCloudId), (document) => {
          if (document.exists()) {
            if (isPausedRef.current) {
              return;
            }
            const dataList = document.data().data.filter((x) => !exclusionListRef.current.includes(x.name));

            if (dataList.length > 0) setShowNotification(false);

            dispatchTab({ type: 'UPDATE_TAB', data: dataList });

            dispatch({ type: 'UPDATE_DATA', data: dataList });

            setTotalResponders(document.data().noOfResponders || 0);
          }
        }, (error) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR'))
      });
    }

    return () => {
      unsubscribeFromFirestore();
    };
  }, [wordCloudId, isPausedRef.current]);

  const onPauseClick = () => {
    const { charts } = Highcharts;
    const chart = charts[charts.length - 1];
    chart.series[0].chart.delImgs = [];
  };

  const deleteComment = async (text) => {
    /**
       We don't want to get the wrong index as data keeps on changing on Moderation Mode.
       Therefore, we pause it while the deletion process is on.
     */
    if (moderationModeRef.current) setIsPaused(true);
    const res = await updateWordCloud({ id: wordCloudId, data: { streamId, word: text?.toLowerCase() } });
    if (res.status) {
      const { charts } = Highcharts;
      const chart = charts[charts.length - 1];
      const cComments = chart.series[0].data.map((comment) => comment.name);
      const index = cComments.indexOf(text);
      if (index > -1) {
        const wordToDelete = chart.series[0].data[index];
        setExclusionList(wordToDelete.name?.toLowerCase());
        wordToDelete.graphic.hide();
      }
    } else {
      setMessages([res.message]);
    }
    if (moderationModeRef.current) setIsPaused(false);
    return res;
  };

  const onPlayClick = () => {
    const { charts } = Highcharts;
    const chart = charts[charts.length - 1];
    chart?.series[0]?.chart?.delImgs?.forEach((img) => {
      img.hide();
    });
    if (chart?.series[0]?.chart?.delImgs) { chart.series[0].chart.delImgs = []; }
  };

  const SendDataToGoogleAnalytics = () => {
    const { charts } = Highcharts;
    const chart = charts[charts.length - 1];
    pushDataLayerForEvent('end_wonder_words_interaction', { no_comments: chart?.series[0]?.data?.length });
  };

  const stopWordCloud = async () => {
    unsubscribeFromFirestore();
    // const sessionId = localStorage.getItem('sessionId');
    // await stopEngagement(streamId, 'wordCloud', { engagementId: wordCloudId, platformType, sessionId });
  };

  useEffect(() => {
    if (activeInteractionRef.current?.id && activeInteractionRef.current?.type === 'wordCloud') {
      setupWordCloud({
        id: activeInteractionRef.current.id,
        colors: activeInteractionRef.current.colors,
        settings: activeInteractionRef.current.settings,
        noOfResponders: activeInteractionRef.current.noOfResponders || 0,
        interactionResult: activeInteractionRef.current.currentRosResult || []
      });
    }
  }, [activeInteractionRef.current]);

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleWordCloud({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult });
    } else {
      handleWordCloud({ interactionId: null, interactionResult: [] });
    }
    setStartTime(Date.now());
  };

  const handleStopInteraction = () => {
    stopWordCloud();
    setIsPaused(false);
    onPlayClick();
    SendDataToGoogleAnalytics();
    setWordCloudId('');
  };

  const manageWordCloudInteraction = () => {
    if (!isResult && startInteraction) {
      handleStartInteraction();
    } else if (wordCloudId) {
      handleStopInteraction();
    } else if (currentRosResult && currentRosResult.length > 0) {
      setTotalResponders(currentRosResult[0].noOfResponders || 0);
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;

    setShowRosModel(false);
    if (moderationModeRef.current) return;

    manageWordCloudInteraction();

    setOverrideDismissStatus(new Date().getTime());
  }, [currentRosFeature.id, startInteraction]);

  const handleEditBtn = () => {
    if (isPaused) {
      setIsPaused(false); onPlayClick();
    } else {
      setIsPaused(true); onPauseClick();
    }
  };

  useImperativeHandle(ref, () => ({
    handleEditBtn,
    deleteComment,
    async triggerEngagement({ interactionResult }) {
      if (interactionResult && interactionResult.length > 0) {
        await handleWordCloud({ interactionId: interactionResult[0].id, interactionResult });
      }
    }
  }));

  useEffect(() => {
    if (!splitDiv) return;
    window.dispatchEvent(new Event('resize'));
    dispatch({ type: 'UPDATE_DATA', data: comments });
  }, [splitDiv]);

  return (
    <div className={`${showInstantCustomisationPopup ? 'calc(100% - 440px)' : 'w-full'} h-full overflow-hidden rounded-lg flex`}>
      <WordChart
        backgroundColor={'rgba(255, 255, 255, 0)'}
        highchartsRef={highchartsRef}
        chartOptions={chartOptions}
        isFullScreen={isFullScreen}
        splitDiv={splitDiv}
        containerProps={{ style: { width: '100%', minHeight: '100%' } }}
        showInstantCustomisationPopup={showInstantCustomisationPopup}
      />
    </div>
  );
});

export default Wordcloud;
